/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { ShowcaseTab } from 'walden-fast/components/showcase-tabs';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type ModalService from 'walden-fast/services/modal';
import type RouterService from '@ember/routing/router-service';
import type { ShowcaseAction } from 'walden-fast/types/showcase-actions';

export interface ShowcaseContentTab extends ShowcaseTab {
  title: string;
  description: string;
  image: string;
  primaryCta: ShowcaseAction;
  secondaryCta?: ShowcaseAction;
}

export interface ShowcaseContentSignature {
  Args: {
    tabs: ShowcaseContentTab[];
  };
  Blocks: {
    default: [];
    title: [];
    description: [];
    image: [];
  };
}

export default class ShowcaseContentComponent extends Component<ShowcaseContentSignature> {
  @tracked activeTab: ShowcaseTab = this.args.tabs[0] as ShowcaseTab;
  @tracked isTransitioning: boolean = false;
  @service('modal') declare modal: ModalService;
  @service('router') declare routerService: RouterService;

  contentWrapperElement: HTMLElement | null = null;
  lastEventTime: number = 0;
  eventDebounceTime: number = 500;
  touchStartY: number = 0;
  touchThreshold: number = 50;

  getContentWrapper() {
    if (!this.contentWrapperElement) {
      this.contentWrapperElement = document.getElementById(
        'tab-content-wrapper',
      );
    }
    return this.contentWrapperElement;
  }

  @action
  handleWheel(event: WheelEvent) {
    event.preventDefault();
    if (this.isTransitioning) {
      return;
    }

    const currentTime = performance.now();
    if (currentTime - this.lastEventTime < this.eventDebounceTime) {
      return;
    }
    this.lastEventTime = currentTime;

    this.handleNavigation(event.deltaY > 0);
  }

  @action
  handleTouchStart(event: TouchEvent) {
    if (event.touches && event.touches.length === 1) {
      this.touchStartY = event.touches[0]?.clientY ?? 0;
    }
  }

  @action
  handleTouchMove(event: TouchEvent) {
    event.preventDefault();
  }

  @action
  handleTouchEnd(event: TouchEvent) {
    if (this.isTransitioning) {
      return;
    }

    const currentTime = performance.now();
    if (currentTime - this.lastEventTime < this.eventDebounceTime) {
      return;
    }

    if (event.changedTouches && event.changedTouches.length === 1) {
      const touchEndY = event.changedTouches[0]?.clientY ?? 0;
      const deltaY = this.touchStartY - touchEndY;

      if (Math.abs(deltaY) > this.touchThreshold) {
        this.lastEventTime = currentTime;
        this.handleNavigation(deltaY > 0);
      }
    }
  }

  handleNavigation(moveForward: boolean) {
    const currentIndex = this.args.tabs.findIndex(
      (tab) => tab.value === this.activeTab.value,
    );

    if (moveForward) {
      if (currentIndex < this.args.tabs.length - 1) {
        const nextTab = this.args.tabs[currentIndex + 1];
        if (nextTab) {
          this.changeTab(nextTab);
        }
      }
    } else {
      if (currentIndex > 0) {
        const prevTab = this.args.tabs[currentIndex - 1];
        if (prevTab) {
          this.changeTab(prevTab);
        }
      }
    }
  }

  @action
  changeTab(tab: ShowcaseTab) {
    if (this.activeTab.value === tab.value || this.isTransitioning) {
      return;
    }

    this.activeTab = tab;
    const tabIndex = this.args.tabs.findIndex((t) => t.value === tab.value);
    const contentWrapper = this.getContentWrapper();

    if (tabIndex !== -1 && contentWrapper) {
      this.isTransitioning = true;
      const scrollTo = tabIndex * contentWrapper.clientHeight;
      this.springScrollTo(contentWrapper, scrollTo, 100);
    }
  }

  activateServiceModalName = 'activate-service';

  @action
  async doAction(actionConfig: ShowcaseAction) {
    switch (actionConfig.type) {
      case 'activate-service':
        this.modal.open(this.activateServiceModalName);
        break;
      case 'email':
        window.location.href = `mailto:${actionConfig.value}`;
        break;
      case 'external-link':
        window.open(actionConfig.value, '_blank', 'noopener,noreferrer');
        break;
      case 'internal-route':
        await this.routerService.transitionTo(actionConfig.value);
        break;
      default: {
        const exhaustiveCheck: never = actionConfig.type;
        throw new Error('Unhandled action type: ' + String(exhaustiveCheck));
      }
    }
  }

  springScrollTo(element: HTMLElement, to: number, duration: number) {
    const start = element.scrollTop;
    const change = to - start;
    const startTime = performance.now();

    const easeInOutCubic = (t: number) => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animateScroll = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const eased = easeInOutCubic(progress);
      element.scrollTop = start + change * eased;

      if (elapsedTime < duration) {
        requestAnimationFrame(animateScroll);
      } else {
        this.isTransitioning = false;
      }
    };

    requestAnimationFrame(animateScroll);
  }

  checkUneven(index: number): boolean {
    return index % 2 !== 0;
  }
}
